import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../Components/auth/firebaseConfig';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../Components/context/SearchContext'; // Correct import
import SideDrawer from '../Components/newcomponents/SideDrawer';
import { fetchUserGroups, fetchUserDetails, fetchAssignedGroupsByEmail, fetchBoards, deleteBoard } from '../DatabaseApis/DBQuery';
import { useParams } from 'react-router-dom';
import { getCookie } from '../utils/cookieUtils';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: 'none',
}));

function BoardAccess() {
  const { projectId } = useParams();
  const [boards, setBoards] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [editingBoard, setEditingBoard] = useState(null);
  const [newAccessGroups, setNewAccessGroups] = useState({});
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState('');
  // const [userRole, setUserRole] = useState('');
  // const [userId, setUserId] = useState();
  const [assignedGroups, setAssignedGroups] = useState([]);
  const { searchTerm } = useSearch(); // Correct usage
  const navigate = useNavigate();

  const userData = getCookie('userData');

  useEffect(() => {
        const getUserDetails = async () => {
            const token = userData?.token;
    
            if (!token) {
              console.error('Token not found in session storage');
              return;
            }
  
            try {
              const data = await fetchUserDetails(token);
              setEmail(data.email);
              setTenantId(data.tenantId);
              // setUserRole(data.role);
            }
           catch (error) {
            console.error('Error fetching user details:', error);
          }
        };
    
        getUserDetails();
      }, []);

      const getAssignedGroups = useCallback(async () => {
          const result = await fetchAssignedGroupsByEmail(email);
          // setUserId(result.userId);
          return result.assignedGroups;
      }, [email])

    const getUserGroups = useCallback(async () => {
      try {
        const groupsData = await fetchUserGroups();
        setUserGroups(groupsData);
      } catch (error) {
        console.error("Error loading user groups:", error);
      }
    }, []);

  useEffect(() => {
    getAssignedGroups();
    getUserGroups();
  }, []);

  useEffect(() => {
    const getBoards = async () => {
      try {
        let groups = assignedGroups;
          if (groups.length === 0) {
            groups = await getAssignedGroups();
            setAssignedGroups(groups);
          }
        const boardsCollection = await fetchBoards(projectId, groups, tenantId);
        setBoards(boardsCollection.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    };

    getBoards();
  }, []);

  // Ensure searchTerm is defined and boards have the necessary fields
  const filteredBoards = boards.filter(board => {
    const boardNo = board.boardNo || '';
    const boardName = board.boardName || '';
    const lowerCaseSearchTerm = searchTerm?.toLowerCase() || '';
    return boardNo.toLowerCase().includes(lowerCaseSearchTerm) ||
      boardName.toLowerCase().includes(lowerCaseSearchTerm);
  });

  const handleEdit = (boardId) => {
    setEditingBoard(boardId);
    setNewAccessGroups({
      ...newAccessGroups,
      [boardId]: boards.find(board => board.id === boardId)?.accessGroups || []
    });
  };

  const handleSave = async (boardId) => {
    try {
      await updateDoc(doc(db, 'boards', boardId), {
        accessGroups: newAccessGroups[boardId]
      });
      setEditingBoard(null);
    } catch (error) {
      console.error('Error saving board access groups:', error);
    }
  };

  const handleCancel = () => {
    setEditingBoard(null);
  };

  const handleDelete = async (boardId) => {
    try {
      await deleteBoard(boardId, tenantId);
      setBoards(boards.filter(board => board.id !== boardId));
    } catch (error) {
      console.error('Error deleting board:', error);
    }
  };

  const handleAddClick = () => {
    navigate('/add-board');
  };

  const handleAccessGroupChange = (boardId, groupId, checked) => {
    const updatedGroups = checked
      ? [...(newAccessGroups[boardId] || []), groupId]
      : (newAccessGroups[boardId] || []).filter(id => id !== groupId);
    setNewAccessGroups({
      ...newAccessGroups,
      [boardId]: updatedGroups
    });
  };

  return (
    <Box sx={{ marginLeft: '70px' }}>
      <Typography variant="h4" gutterBottom>
        Board Access
      </Typography>
      <SideDrawer/>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Board No#</StyledTableCell>
              <StyledTableCell>Board Name</StyledTableCell>
              <StyledTableCell>Access Groups</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBoards.map(board => (
              <TableRow key={board.id}>
                <StyledTableCell>{board.boardNo || 'N/A'}</StyledTableCell>
                <StyledTableCell>{board.boardName || 'N/A'}</StyledTableCell>
                <StyledTableCell>
                  {editingBoard === board.id ? (
                    userGroups.map(group => (
                      <div key={group.id}>
                        <label>
                          <input
                            type="checkbox"
                            checked={(newAccessGroups[board.id] || []).includes(group.id)}
                            onChange={(e) =>
                              handleAccessGroupChange(board.id, group.id, e.target.checked)
                            }
                          />
                          {group.groupName}
                        </label>
                      </div>
                    ))
                  ) : (
                    (board.accessGroups || []).map(groupId => (
                      userGroups.find(group => group.id === groupId)?.groupName || groupId
                    )).join(', ')
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  {editingBoard === board.id ? (
                    <>
                      <IconButton color="primary" aria-label="save" onClick={() => handleSave(board.id)}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton color="secondary" aria-label="cancel" onClick={handleCancel}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton color="primary" aria-label="edit" onClick={() => handleEdit(board.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="secondary"  style={{ color: 'red' }} aria-label="delete" onClick={() => handleDelete(board.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <IconButton
        color="primary"
        onClick={handleAddClick}
        sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: 'primary.main', color: 'white' }}
      >
        <AddIcon />
      </IconButton>
    </Box>
  );
}

export default BoardAccess;
